import { Text } from '@cretesuite/design-system'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import {
  arrowBackOutline,
  chevronDownOutline,
  chevronUpOutline,
  pencilOutline,
} from 'ionicons/icons'
import { useEffect, useMemo, useRef, useState } from 'react'
import { type FieldPath, useFieldArray, useForm } from 'react-hook-form'
import { useMutation, useQuery } from 'urql'
import { Input } from '../components/form/Input'
import { Textarea } from '../components/form/Textarea'
import { Time } from '../components/form/Time'
import { fieldStyles as sharedFieldStyles } from '../components/form/fieldStyles'
import type { GetOrderWorkTicketDataQuery } from '../gql/graphql'
import {
  GetOrderWorkTicketData,
  GetWorkTicketInventory,
  SubmitWorkTicket,
} from '../graphql/workTicket'
import { submitQueueable } from '../helpers/submitQueueable'
import { timeAfterValidator } from '../helpers/timeSequence'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { useFeature } from '../hooks/useFeatures'
import { useNetwork } from '../hooks/useNetwork'
import { useToast } from '../hooks/useToast'
import { WorkTicketSignatureModal } from './WorkTicketSignatureModal'

/* ─────────────
   Form Types
   ───────────── */
export interface FormValues {
  arrivalTime?: string
  departureTime?: string
  comment?: string
  pours: {
    scheduleIndex: number
    startTime?: string
    endTime?: string
    cubicYards?: string
  }[]
  inventory: {
    itemId: number
    quantity?: string
  }[]
}

interface WorkTicketModalProps {
  orderId: number
  onDismiss?: () => void
}

/* ─────────────────────
   Field-level styles (shared base + modal-specific collapse styles)
   ───────────────────── */
const fieldStyles = {
  ...sharedFieldStyles,
  collapseHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 0',
    cursor: 'pointer',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
  } as React.CSSProperties,
  collapseLabel: {
    fontSize: '14px',
    fontWeight: 700,
    color: 'var(--cs-color-text, #1a1a1a)',
  } as React.CSSProperties,
  collapseSub: {
    fontSize: '13px',
    color: 'var(--cs-color-text-secondary, #999)',
    marginTop: '2px',
  } as React.CSSProperties,
}

/* ═══════════════════════════════════════════
   Main Export: WorkTicketModal
   ═══════════════════════════════════════════ */
export const WorkTicketModal = ({
  orderId,
  onDismiss,
}: WorkTicketModalProps) => {
  const { _ } = useLingui()

  const [orderResult] = useQuery({
    query: GetOrderWorkTicketData,
    variables: { orderId },
  })

  const [inventoryResult] = useQuery({ query: GetWorkTicketInventory })

  const rawOrder = orderResult.data?.order
  const loading = orderResult.fetching || inventoryResult.fetching
  // Only surface an error when the cache can't back the screen — offline reads
  // fall back to the persisted cache, which still has the data.
  const error =
    (orderResult.error && !orderResult.data) ||
    (inventoryResult.error && !inventoryResult.data)

  // Augment order with always-visible tenant inventory (same as V1's
  // augmentOrderWithAlwaysVisibleInventory in useWorkTicket.tsx)
  const order = useMemo(() => {
    if (!rawOrder) return rawOrder
    const tenantItems = (inventoryResult.data?.inventory || []).filter(
      (item) => item.branchId === rawOrder.branchId,
    )

    const currentInventory = [...(rawOrder.planned?.inventory || [])]
    const existingIds = new Set(currentInventory.map((inv) => inv.item.id))

    // Only inject items with operatorWorkTicket === 'always'
    const alwaysVisible = tenantItems.filter(
      (item) => item.visibility?.operatorWorkTicket === 'always',
    )

    alwaysVisible.forEach((item, index) => {
      if (existingIds.has(item.id)) return
      currentInventory.push({
        id: index * -1,
        quantity: undefined,
        item,
      })
    })

    return {
      ...rawOrder,
      planned: rawOrder.planned
        ? { ...rawOrder.planned, inventory: currentInventory }
        : rawOrder.planned,
    }
  }, [rawOrder, inventoryResult.data])

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot='start'>
            <IonButton onClick={onDismiss}>
              <IonIcon icon={arrowBackOutline} />
            </IonButton>
          </IonButtons>
          <IonTitle style={{ fontWeight: 700, fontSize: '17px' }}>
            {_(msg`Work Ticket`)}
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        {loading && (
          <div
            className='ion-text-center ion-padding'
            style={{ marginTop: 40 }}
          >
            <Text variant='body'>{_(msg`Loading...`)}</Text>
          </div>
        )}
        {error && (
          <div className='ion-padding'>
            <Text variant='body' style={{ color: 'var(--ion-color-danger)' }}>
              {_(msg`Sorry an error occurred, please refresh and try again`)}
            </Text>
          </div>
        )}
        {order && (
          <WorkTicketForm key={order.id} order={order} onDismiss={onDismiss} />
        )}
      </IonContent>
    </>
  )
}

/* ═══════════════════════════════════════════
   Inner Form (rendered only after data loads)
   ═══════════════════════════════════════════ */
export type OrderData = NonNullable<GetOrderWorkTicketDataQuery['order']>
type OrderDetails = NonNullable<OrderData['planned']>
type OrderInventoryItem = OrderDetails['inventory'][number]
type ScheduleEntry = OrderDetails['schedule'][number]

/** Whether this order already carries a collected work-ticket signature. */
const isWorkTicketSigned = (order: OrderData): boolean =>
  Boolean(
    order.signature?.metadata?.some(
      (m) => m.key === 'workTicket' && m.value === true,
    ),
  )

/** The operator's own work-ticket comment on this order, if any. */
export const existingWorkTicketComment = (order: OrderData, userId?: number) =>
  order.comments.find(
    (c) =>
      c.metadata.some((m) => m.key === 'workticketComment') &&
      c.personId === userId,
  )

/**
 * Quantities pre-fill ONLY from the operator's own entered actuals — never
 * the planned amounts (operators must enter the real used amounts, not
 * assume the system filled them). v1 parity: Primer defaults to 1 once
 * actuals exist and it wasn't entered.
 */
export const defaultInventoryQuantities = (
  operatorDetails: OrderData['actuals'][number] | undefined,
  visibleItems: OrderInventoryItem[],
  hasActuals: boolean,
) => {
  const qtyById: Record<number, number | null | undefined> = {}
  for (const inv of operatorDetails?.inventory || []) {
    qtyById[inv.item.id] = inv.quantity
  }

  const primer = visibleItems.find(
    (i) => i.item.name.toLowerCase() === 'primer',
  )
  if (hasActuals && primer && qtyById[primer.item.id] == null) {
    qtyById[primer.item.id] = 1
  }

  return qtyById
}

const WorkTicketForm = ({
  order,
  onDismiss,
}: {
  order: OrderData
  onDismiss?: () => void
}) => {
  const { _ } = useLingui()
  const { user } = useCurrentUser()
  const { isOnline } = useNetwork()
  const [presentToast] = useToast()
  const [additionalItemsOpen, setAdditionalItemsOpen] = useState(false)
  const [showSignatureModal, setShowSignatureModal] = useState(false)

  const [, submitWorkTicket] = useMutation(SubmitWorkTicket)

  const editable = useMemo(() => {
    const status = order.statusDetails.slug || order.status
    return !['reviewed', 'cancelled'].includes(status)
  }, [order])

  // Signatures are collected in the customer-app now — the crew-app no longer
  // requires them, regardless of the workTicketRequireSignature flag.
  const crewCollectsSignatures = false
  const wasSignedOnLoad = useRef(isWorkTicketSigned(order))
  const workTicketRequireSignature = useFeature('workTicketRequireSignature')
  const requireSignature =
    crewCollectsSignatures &&
    workTicketRequireSignature &&
    !wasSignedOnLoad.current &&
    isOnline

  const useCubicMeters = useFeature('localeUseCubicMeters')

  /* ─── Compute default values from loaded order ─── */
  const defaultValues = useMemo((): FormValues => {
    const operatorDetails = order.actuals.find((a) => a.subType === 'operator')
    // Only the operator's OWN previously-entered times pre-fill the fields; the
    // planned schedule never does (it's offered as the picker default instead).
    const hasActuals = !!operatorDetails
    const details = operatorDetails || order.planned

    const schedule: ScheduleEntry[] = details?.schedule || []
    const pourSchedule = schedule.filter(
      (s) => s.step === 'pour' || s.stepDetails.slug === 'pour',
    )
    const firstEntry = schedule[0]
    const lastEntry = schedule[schedule.length - 1]

    // Collect + dedup inventory from order details
    const allInventory: OrderInventoryItem[] = [
      ...(details?.inventory || []),
      ...(order.planned?.inventory || []),
    ]
    const uniqueItems = Array.from(
      new Map(allInventory.map((i) => [i.item.id, i])).values(),
    )
    const visibleItems = uniqueItems.filter(
      (i) => i.item.visibility.operatorWorkTicket !== 'hidden',
    )

    const qtyById = defaultInventoryQuantities(
      operatorDetails,
      visibleItems,
      hasActuals,
    )

    const existingComment = existingWorkTicketComment(order, user?.id)

    return {
      arrivalTime: hasActuals ? firstEntry?.startTime || undefined : undefined,
      departureTime: hasActuals ? lastEntry?.endTime || undefined : undefined,
      pours:
        pourSchedule.length > 0
          ? pourSchedule.map((p) => ({
              scheduleIndex: p.scheduleIndex,
              startTime: hasActuals ? p.startTime || undefined : undefined,
              endTime: hasActuals ? p.endTime || undefined : undefined,
              cubicYards: hasActuals
                ? p.metrics
                    .find((m) => m.key === 'cubicYards')
                    ?.value?.toString() || ''
                : '',
            }))
          : [
              {
                scheduleIndex: 1,
                startTime: undefined,
                endTime: undefined,
                cubicYards: '',
              },
            ],
      inventory: visibleItems.map((i) => ({
        itemId: i.item.id,
        quantity: qtyById[i.item.id]?.toString() || '',
      })),
      comment: existingComment?.text || '',
    }
  }, [order, user])

  // Planned times offered as each picker's wheel default (the fields stay blank
  // until the operator confirms/adjusts). Pour times align by index with the
  // `pours` field array above.
  const plannedTimes = useMemo(() => {
    const sched = order.planned?.schedule ?? []
    const pours = sched.filter(
      (s) => s.step === 'pour' || s.stepDetails.slug === 'pour',
    )
    return {
      arrival: sched[0]?.startTime ?? undefined,
      departure: sched[sched.length - 1]?.endTime ?? undefined,
      pours: pours.map((p) => ({
        start: p.startTime ?? undefined,
        end: p.endTime ?? undefined,
      })),
    }
  }, [order])

  /* ─── react-hook-form ─── */
  const {
    control,
    handleSubmit,
    register,
    getValues,
    formState: { errors, isSubmitting, isSubmitSuccessful, isDirty },
  } = useForm<FormValues>({ defaultValues })

  // Times must be non-decreasing top-to-bottom (v1 parity):
  // arrival ≤ pour start ≤ pour end ≤ … ≤ departure
  const timeAfter = (previousField: FieldPath<FormValues>) =>
    timeAfterValidator(
      () => getValues(previousField) as string | undefined,
      _(msg`You must select a time that occurs after the field above.`),
    )

  const { fields: pourFields } = useFieldArray({ control, name: 'pours' })
  const { fields: inventoryFields } = useFieldArray({
    control,
    name: 'inventory',
  })

  /* ─── Inventory item lookup ─── */
  const inventoryItemsById = useMemo(() => {
    const byId: Record<
      string,
      {
        id: number
        name: string
        category?: string | null
        displayUnit?: string | null
      }
    > = {}
    const collect = ({ item }: { item: (typeof byId)[string] }) => {
      byId[item.id] = item
    }
    order?.planned?.inventory?.forEach(collect)
    if (order?.actuals) {
      for (const d of order.actuals) {
        d.inventory?.forEach(collect)
      }
    }
    return byId
  }, [order])

  /* ─── Partition: system vs additional ─── */
  const { systemItems, additionalItems } = useMemo(() => {
    const sys: { field: (typeof inventoryFields)[0]; index: number }[] = []
    const add: { field: (typeof inventoryFields)[0]; index: number }[] = []
    inventoryFields.forEach((field, index) => {
      const item = inventoryItemsById[field.itemId]
      if (!item) return
      if (item.category === 'system') sys.push({ field, index })
      else add.push({ field, index })
    })
    return { systemItems: sys, additionalItems: add }
  }, [inventoryFields, inventoryItemsById])

  /* ─── Save partial values (before signature) ─── */
  const savePartialValues = async (data: FormValues) => {
    const variables = formValuesToMutationPayload(data, order, user?.id)
    const exec = submitWorkTicket(variables)

    // Never block on the network: the optimistic update already reflects the
    // change. Offline or a too-slow connection → queue and continue; a
    // responsive connection still surfaces (and stops on) real server errors.
    const outcome = await submitQueueable(exec, { isOnline })
    if (outcome.status === 'queued') {
      presentToast({
        message: _(msg`Saved. It will sync when you're back online.`),
        duration: 3000,
      })
      return
    }
    if (outcome.status === 'error') {
      const err = outcome.result.error
      const errMsg = err?.message ?? _(msg`An error occurred`)
      presentToast({
        color: 'danger',
        message: `${_(msg`Error`)}: ${errMsg}. ${_(msg`Please try again`)}`,
        duration: 4000,
      })
      throw err
    }
  }

  /* ─── Save work-in-progress when the modal closes (v1 parity) ───
     Bypasses validation on purpose — half-entered times must survive a close —
     and never advances the order status (`status: undefined`). */
  const submittedRef = useRef(false)
  const saveWorkInProgress = () => {
    if (!editable || submittedRef.current) return
    // Offline, isSubmitting stays true until the queued mutation flushes —
    // only trust these flags online (v1 behavior).
    if (isOnline && (isSubmitting || isSubmitSuccessful)) return
    if (!isDirty) return

    const variables = formValuesToMutationPayload(getValues(), order, user?.id)
    variables.data.status = undefined
    // Nothing actually entered — skip the meaningless network call
    if (isEmptyWorkTicketChangeset(variables)) return
    // Fire and forget: the offline queue / optimistic update owns the outcome
    submitWorkTicket(variables)
  }
  const saveWorkInProgressRef = useRef(saveWorkInProgress)
  useEffect(() => {
    saveWorkInProgressRef.current = saveWorkInProgress
  })
  // The modal's contents unmount on dismiss — persist WIP right before that
  useEffect(() => () => saveWorkInProgressRef.current(), [])

  /* ─── Full submit (after signature) ─── */
  const onFormSubmit = async (data: FormValues) => {
    submittedRef.current = true
    try {
      await savePartialValues(data)
    } catch (err) {
      submittedRef.current = false
      throw err
    }
    if (isOnline) {
      presentToast({
        color: 'success',
        message: _(msg`Work ticket saved successfully.`),
        duration: 4000,
      })
    }
    onDismiss?.()
  }

  const onInvalid = (formErrors: typeof errors) => {
    const names = ['arrivalTime']
    pourFields.forEach((_, i) => {
      names.push(`pours.${i}.startTime`, `pours.${i}.endTime`)
    })
    names.push('departureTime')
    const getByPath = (obj: unknown, path: string): unknown =>
      path
        .split('.')
        .reduce<unknown>(
          (o, k) => (o == null ? undefined : (o as Record<string, unknown>)[k]),
          obj,
        )
    const first = names.find((n) => getByPath(formErrors, n))
    if (!first) return
    setTimeout(() => {
      const el = document.querySelector(`[data-field="${first}"]`)
      if (el instanceof HTMLElement) el.click()
    }, 0)
  }

  /* ─── Open signature flow ─── */
  const handleCollectSignature = async () => {
    // react-hook-form's handleSubmit resolves (without throwing) when validation
    // fails — it just skips the valid callback. So gate on a flag set *inside*
    // the valid callback; only open the signature flow once the form is both
    // valid and saved. Validation errors render via `errors` for the user.
    let saved = false
    try {
      await handleSubmit(async (data) => {
        await savePartialValues(data)
        saved = true
      })()
    } catch {
      // save error — already surfaced via toast in savePartialValues
      return
    }
    if (saved) setShowSignatureModal(true)
  }

  const handleSignatureDismiss = (completed?: boolean) => {
    setShowSignatureModal(false)
    if (completed) {
      onDismiss?.()
    }
  }

  /* ─── Render ─── */
  return (
    <form
      id='work-ticket-form'
      onSubmit={handleSubmit(onFormSubmit, onInvalid)}
      style={{ padding: '0 16px 24px' }}
    >
      {/* ── Arrived on Site ── */}
      <Time
        label={_(msg`Arrived on Site`)}
        placeholder={_(msg`Time you arrived on site.`)}
        disabled={!editable}
        control={control}
        name='arrivalTime'
        plannedTime={plannedTimes.arrival}
        rules={{
          required: _(msg`Please enter the time you arrived on site.`),
        }}
      />
      {errors.arrivalTime && (
        <span style={fieldStyles.errorText}>{errors.arrivalTime.message}</span>
      )}

      {/* ── Pour fields ── */}
      {pourFields.map((field, index) => {
        const suffix = pourFields.length > 1 ? ` #${index + 1}` : ''
        const pourErrors = errors.pours as
          | Array<Record<string, { message?: string }>>
          | undefined
        return (
          <div key={field.id}>
            <Time
              label={`${_(msg`Started Pour`)}${suffix}`}
              placeholder={_(msg`Time you started the pour.`)}
              disabled={!editable}
              control={control}
              name={`pours.${index}.startTime`}
              plannedTime={plannedTimes.pours[index]?.start}
              rules={{
                required: _(msg`Please enter the time you started the pour.`),
                validate: timeAfter(
                  index === 0 ? 'arrivalTime' : `pours.${index - 1}.endTime`,
                ),
              }}
            />
            {pourErrors?.[index]?.startTime && (
              <span style={fieldStyles.errorText}>
                {pourErrors[index].startTime.message}
              </span>
            )}

            <Time
              label={`${_(msg`Finished Pour`)}${suffix}`}
              placeholder={_(msg`Time you finished the pour.`)}
              disabled={!editable}
              control={control}
              name={`pours.${index}.endTime`}
              plannedTime={plannedTimes.pours[index]?.end}
              rules={{
                required: _(msg`Please enter the time you finished the pour.`),
                validate: timeAfter(`pours.${index}.startTime`),
              }}
            />
            {pourErrors?.[index]?.endTime && (
              <span style={fieldStyles.errorText}>
                {pourErrors[index].endTime.message}
              </span>
            )}

            <div style={fieldStyles.item}>
              <span style={fieldStyles.label}>
                {useCubicMeters
                  ? _(msg`Cubic Meters Pumped`)
                  : _(msg`Cubic Yards Pumped`)}
                {suffix && ` ${_(msg`Pour`)}${suffix}`}
              </span>
              <Input
                type='text'
                inputMode='decimal'
                disabled={!editable}
                placeholder={
                  useCubicMeters
                    ? _(msg`How many cubic meters were pumped.`)
                    : _(msg`How many yards were pumped.`)
                }
                {...register(`pours.${index}.cubicYards`, {
                  required: useCubicMeters
                    ? _(msg`Please enter how many cubic meters were pumped.`)
                    : _(msg`Please enter how many yards were pumped.`),
                })}
              />
              {pourErrors?.[index]?.cubicYards && (
                <span style={fieldStyles.errorText}>
                  {pourErrors[index].cubicYards.message}
                </span>
              )}
            </div>
          </div>
        )
      })}

      {/* ── Departed Site At ── */}
      <Time
        label={_(msg`Departed Site At`)}
        placeholder={_(msg`Time you departed the site.`)}
        disabled={!editable}
        control={control}
        name='departureTime'
        plannedTime={plannedTimes.departure}
        rules={{
          required: _(msg`Please enter the time you departed the site.`),
          validate: timeAfter(
            pourFields.length === 0
              ? 'arrivalTime'
              : `pours.${pourFields.length - 1}.endTime`,
          ),
        }}
      />
      {errors.departureTime && (
        <span style={fieldStyles.errorText}>
          {errors.departureTime.message}
        </span>
      )}

      {/* ── System Inventory ── */}
      {systemItems.map(({ field, index }) => {
        const item = inventoryItemsById[field.itemId]
        if (!item) return null
        return (
          <div key={field.id} style={fieldStyles.item}>
            <span style={fieldStyles.label}>System - {item.name}</span>
            <Input
              type='text'
              inputMode='decimal'
              disabled={!editable}
              placeholder={
                item?.displayUnit
                  ? `${_(msg`How many`)} ${item.displayUnit} ${_(msg`were used.`)}`
                  : _(msg`How much was used.`)
              }
              {...register(`inventory.${index}.quantity`)}
            />
          </div>
        )
      })}

      {/* ── Additional Items (Collapsible) ── */}
      {additionalItems.length > 0 && (
        <>
          <button
            type='button'
            style={{
              ...fieldStyles.buttonReset,
              ...fieldStyles.collapseHeader,
            }}
            onClick={() => setAdditionalItemsOpen(!additionalItemsOpen)}
          >
            <div>
              <div style={fieldStyles.collapseLabel}>
                {_(msg`Additional items`)}
              </div>
              {!additionalItemsOpen && (
                <div style={fieldStyles.collapseSub}>
                  {additionalItems
                    .map(({ field }) => inventoryItemsById[field.itemId]?.name)
                    .filter(Boolean)
                    .join(', ')}
                </div>
              )}
            </div>
            <IonIcon
              icon={additionalItemsOpen ? chevronUpOutline : chevronDownOutline}
              style={{
                fontSize: '20px',
                color: 'var(--cs-color-text-secondary, #999)',
              }}
            />
          </button>
          {additionalItemsOpen &&
            additionalItems.map(({ field, index }) => {
              const item = inventoryItemsById[field.itemId]
              if (!item) return null
              return (
                <div key={field.id} style={fieldStyles.item}>
                  <span style={fieldStyles.label}>{item.name}</span>
                  <Input
                    type='text'
                    inputMode='decimal'
                    disabled={!editable}
                    placeholder={
                      item?.displayUnit
                        ? `${_(msg`How many`)} ${item.displayUnit} ${_(msg`were used.`)}`
                        : `${_(msg`Enter a number for`)} ${item.name.toLowerCase()}.`
                    }
                    {...register(`inventory.${index}.quantity`)}
                  />
                </div>
              )
            })}
        </>
      )}

      {/* ── Comments ── */}
      <div style={fieldStyles.item}>
        <span style={fieldStyles.label}>{_(msg`Comments`)}</span>
        <Textarea
          disabled={!editable}
          placeholder={_(msg`Additional info regarding this job.`)}
          rows={3}
          {...register('comment')}
        />
      </div>

      {/* ── Submit / Collect Signature Button ──
          v1 parity: require a signature only when the feature flag is on and the
          ticket isn't already signed (and online); otherwise submit directly. */}
      {editable && (
        <div style={{ margin: '28px 0 8px', textAlign: 'center' }}>
          <button
            type='button'
            disabled={isSubmitting}
            onClick={
              requireSignature
                ? handleCollectSignature
                : handleSubmit(onFormSubmit, onInvalid)
            }
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              width: '100%',
              padding: '16px 24px',
              fontSize: '16px',
              fontWeight: 700,
              fontFamily: 'inherit',
              color: 'var(--cs-color-primary-foreground, #fff)',
              backgroundColor: 'var(--cs-color-primary, #f5a623)',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              transition: 'background-color 0.2s, transform 0.1s',
              boxShadow: '0 4px 14px rgba(245,166,35,0.35)',
              ...(isSubmitting ? { opacity: 0.6 } : {}),
            }}
          >
            {requireSignature && (
              <IonIcon icon={pencilOutline} style={{ fontSize: '20px' }} />
            )}
            {requireSignature
              ? _(msg`Collect Ticket Signature`)
              : _(msg`Submit Ticket`)}
          </button>
        </div>
      )}

      {/* ── Work Ticket Signature Modal ── */}
      <IonModal
        isOpen={showSignatureModal}
        onDidDismiss={() => handleSignatureDismiss()}
      >
        <WorkTicketSignatureModal
          orderId={order.id}
          onDismiss={handleSignatureDismiss}
        />
      </IonModal>
    </form>
  )
}

/* ═══════════════════════════════════════════
   Mutation payload builder
   ═══════════════════════════════════════════ */
/**
 * Builds the actuals schedule upsert. Adjacent steps share boundary times
 * (v1 parity): prep ends when the next entered time starts; clean starts at
 * the previous entered time. Step slugs come from the planned schedule when
 * available. Untouched pours are pruned instead of upserting empty steps
 * (matters for WIP saves).
 */
/** Pour entries for the schedule upsert; untouched pours are pruned. */
const buildPourEntries = (
  pours: FormValues['pours'],
  plannedEntry: (index: number) => ScheduleEntry | undefined,
) => {
  const entries: Array<{ data: Record<string, unknown> }> = []
  for (const pour of pours) {
    const cubicYards = pour.cubicYards && Number.parseFloat(pour.cubicYards)
    const hasYards = typeof cubicYards === 'number' && !Number.isNaN(cubicYards)
    if (!pour.startTime && !pour.endTime && !hasYards) continue
    entries.push({
      data: {
        step: plannedEntry(pour.scheduleIndex)?.step || 'pour',
        startTime: pour.startTime || undefined,
        endTime: pour.endTime || undefined,
        metrics: hasYards
          ? { upsert: [{ key: 'cubicYards', value: cubicYards }] }
          : undefined,
      },
    })
  }
  return entries
}

export const buildScheduleUpdate = (data: FormValues, order: OrderData) => {
  const plannedSchedule = order.planned?.schedule || []
  const plannedEntry = (index: number) =>
    plannedSchedule.find((s) => s.scheduleIndex === index)

  const allTimes = [
    data.arrivalTime,
    ...data.pours.flatMap((p) => [p.startTime, p.endTime]),
    data.departureTime,
  ].filter((t): t is string => Boolean(t))

  const scheduleUpdate: Array<{ data: Record<string, unknown> }> = []

  if (data.arrivalTime) {
    scheduleUpdate.push({
      data: {
        step: plannedEntry(0)?.step || 'prep',
        startTime: data.arrivalTime,
        endTime: allTimes[1],
      },
    })
  }

  scheduleUpdate.push(...buildPourEntries(data.pours, plannedEntry))

  if (data.departureTime) {
    scheduleUpdate.push({
      data: {
        step: plannedEntry(plannedSchedule.length || -1)?.step || 'clean',
        startTime: allTimes[allTimes.length - 2],
        endTime: data.departureTime,
      },
    })
  }

  return scheduleUpdate
}

/**
 * Updates the operator's existing work-ticket comment instead of creating a
 * duplicate on every save (v1 parity) — negative ids are optimistic cache
 * entries still awaiting their first submit, so those re-create.
 */
export const buildCommentsInput = (
  data: FormValues,
  order: OrderData,
  userId?: number,
) => {
  const commentText = data.comment?.trim()
  const commentsInput: Record<string, unknown> = {}
  if (!commentText) return commentsInput

  const existingComment = existingWorkTicketComment(order, userId)
  if (existingComment && existingComment.id > 0) {
    commentsInput.update = [
      {
        data: { text: commentText },
        where: { id: existingComment.id },
      },
    ]
  } else {
    commentsInput.create = [
      {
        personId: userId,
        text: commentText,
        metadata: {
          create: [{ key: 'workticketComment', value: true }],
        },
      },
    ]
  }
  return commentsInput
}

export const formValuesToMutationPayload = (
  data: FormValues,
  order: OrderData,
  userId?: number,
) => {
  const scheduleUpdate = buildScheduleUpdate(data, order)
  const commentsInput = buildCommentsInput(data, order, userId)

  const inventorySet = data.inventory
    .filter((item) => item.quantity?.trim())
    .map((item) => ({
      itemId: item.itemId,
      quantity: Number.parseFloat(item.quantity || '0'),
    }))

  return {
    data: {
      revision: -42,
      status: ['reviewed', 'cancelled'].includes(order?.status)
        ? undefined
        : 'turned_in',
      comments: commentsInput,
      actuals: {
        upsert: [
          {
            data: {
              inventory: { set: inventorySet },
              schedule: { set: scheduleUpdate },
            },
            where: { subType: 'operator' },
          },
        ],
      },
    },
    where: { id: order.id },
  }
}

/**
 * True when a built payload carries no actual changes. The WIP autosave uses
 * this to skip a meaningless network call — it never blocks the explicit
 * submit, which is governed solely by the form's own validation rules.
 */
export const isEmptyWorkTicketChangeset = (
  variables: ReturnType<typeof formValuesToMutationPayload>,
): boolean => {
  const changes = variables.data.actuals.upsert[0].data
  return (
    changes.schedule.set.length === 0 &&
    changes.inventory.set.length === 0 &&
    Object.keys(variables.data.comments).length === 0
  )
}

export default WorkTicketModal
